import React from 'react';
import PropTypes from 'prop-types';

import SourceDataButton, {
  pickFields,
  FieldSource,
  FooterSourceToggle,
  useFooterSource,
} from './ChartSourceModal';
import {
  WIDTH,
  HEIGHT,
  CX,
  CY,
  OUTER_R,
  INNER_R,
  parseLocaleNumber,
  polarToXY,
  valueToAngleForRange,
  arcPath,
  needlePath,
  formatWeight,
  formatPercentage,
  isBlankFooterValue,
} from './gaugeGeometry';

/**
 * Spekdikte (SP 3.4) gauge.
 *
 * Five fixed bands of two millimetres:
 *   8-10 | 10-12 | 12-14 | 14-16 | 16-18
 *
 * Each segment shows the share of the klassement rows whose average backfat
 * thickness falls in that band: every row is placed by its field 281 value and
 * counts once, and the band's percentage is its row count over the number of
 * rows that could be placed. Bands with no rows show 0%.
 *
 * Counting rows rather than summing field 275 keeps the bands totalling exactly
 * 100%. Field 275 is the backend's per-row percentage already rounded to a whole
 * number, so summing it drifts - 101% and 98% both occur in real deliveries.
 *
 * The needles: red from klassement_totalen 241 (Gem. SP 3.4), black from the BPG
 * benchmark (drgmbmk DEGEMSD, week -1, DELTMCE = ALL). The briefing named 243
 * for the red needle, but that field is Gem. Vlees % - a meat percentage of
 * ~55-62, which pinned the needle at the right-hand end of the mm dial on
 * every delivery. 241 is the spekdikte total, and is what the footer shows.
 */

// klassement key 281 = 'Gem. Waarden - SP 3.4'.
const SPEKDIKTE_KEY = '281';

export const SPEKDIKTE_MIN = 8;
export const SPEKDIKTE_MAX = 18;
export const SPEKDIKTE_SEGMENT_COUNT = 5;
export const SPEKDIKTE_BAND_WIDTH =
  (SPEKDIKTE_MAX - SPEKDIKTE_MIN) / SPEKDIKTE_SEGMENT_COUNT;

// Each segment labels its upper edge; the 8 at the start of the scale is drawn
// separately.
export const SPEKDIKTE_LABELS = ['10', '12', '14', '16', '18'];

// Less backfat is better (see src/config/annotationData.js: "lage spekdikte
// resulteren in een betere AFI"), so this runs green to red - the inverse of
// the vleesdikte gauge - reusing the palette values already on this page.
export const SPEKDIKTE_COLORS = [
  '#2d6a2d', '#a0d48a', '#F2DCBB', '#e07070', '#8B0000',
];

/**
 * Which band an average spekdikte falls into.
 * Bands are lower-inclusive / upper-exclusive. Below 8 folds into the first
 * band and 18 and above into the last, so no share is silently dropped off
 * either end of the dial.
 */
export function getSpekdikteSegmentIndex(value) {
  for (let i = 1; i < SPEKDIKTE_SEGMENT_COUNT; i++) {
    if (value < SPEKDIKTE_MIN + i * SPEKDIKTE_BAND_WIDTH) {
      return i - 1;
    }
  }
  return SPEKDIKTE_SEGMENT_COUNT - 1;
}

/**
 * Hold a needle inside the dial.
 *
 * Averages should always land on the scale, but a value past either end would
 * otherwise be drawn outside the arc, so it pins to the scale instead.
 */
export function clampNeedleValue(value) {
  return Math.min(SPEKDIKTE_MAX, Math.max(SPEKDIKTE_MIN, value));
}

/**
 * Fold the klassement into the five bands.
 *
 * klassement[i]['281'] = average SP 3.4 for that price class, which places the
 * row in a band. Each row adds one to its band; the band's percentage is then
 * its row count over the total number of placed rows.
 *
 * Rows with no 281 are skipped, and left out of the total as well, so the bands
 * that could be placed still add up to 100%.
 */
export function buildSpekdikteSegments(klassement) {
  const counts = Array(SPEKDIKTE_SEGMENT_COUNT).fill(0);
  let placed = 0;

  if (Array.isArray(klassement)) {
    klassement.forEach((item) => {
      const spekdikte = parseLocaleNumber(item[SPEKDIKTE_KEY], null);
      if (spekdikte == null) {
        return;
      }
      counts[getSpekdikteSegmentIndex(spekdikte)] += 1;
      placed += 1;
    });
  }

  return Array.from({ length: SPEKDIKTE_SEGMENT_COUNT }, (_, i) => ({
    min: SPEKDIKTE_MIN + i * SPEKDIKTE_BAND_WIDTH,
    max: SPEKDIKTE_MIN + (i + 1) * SPEKDIKTE_BAND_WIDTH,
    color: SPEKDIKTE_COLORS[i],
    label: i < SPEKDIKTE_LABELS.length ? SPEKDIKTE_LABELS[i] : null,
    rows: counts[i],
    percentage: placed === 0 ? 0 : (counts[i] / placed) * 100,
  }));
}

// Distinct from the other gauges' filter so the page has no duplicate DOM ids.
const SHADOW_FILTER_ID = 'spekdikte-needle-shadow';
const LABEL_OFFSET = 22;
const NEEDLE_RED = '#dc2626';
const NEEDLE_BLACK = '#000';

function needleAngle(value) {
  return valueToAngleForRange(clampNeedleValue(value), SPEKDIKTE_MIN, SPEKDIKTE_MAX);
}

function SpekdikteGauge({ segments, zValue, bpgValue, showShares = true }) {
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role='img'
      aria-label='Spekdikte gauge'
    >
      <defs>
        <filter id={SHADOW_FILTER_ID} x='-20%' y='-20%' width='140%' height='140%'>
          <feDropShadow dx='1' dy='1' stdDeviation='2' floodOpacity='0.3' />
        </filter>
      </defs>

      <text
        x={polarToXY(valueToAngleForRange(SPEKDIKTE_MIN, SPEKDIKTE_MIN, SPEKDIKTE_MAX), OUTER_R + LABEL_OFFSET).x}
        y={polarToXY(valueToAngleForRange(SPEKDIKTE_MIN, SPEKDIKTE_MIN, SPEKDIKTE_MAX), OUTER_R + LABEL_OFFSET).y}
        textAnchor='middle'
        fontSize='11'
        fontWeight='bold'
        fill='#000'
      >
        {SPEKDIKTE_MIN}
      </text>

      {segments.map((seg, i) => {
        const a1 = valueToAngleForRange(seg.min, SPEKDIKTE_MIN, SPEKDIKTE_MAX);
        const a2 = valueToAngleForRange(seg.max, SPEKDIKTE_MIN, SPEKDIKTE_MAX);
        const mid = polarToXY((a1 + a2) / 2, (OUTER_R + INNER_R) / 2);
        const labelPos = polarToXY(a2, OUTER_R + LABEL_OFFSET);

        return (
          <g key={i}>
            <path
              data-segment={i}
              d={arcPath(a1, a2, OUTER_R, INNER_R)}
              fill={seg.color}
              stroke='white'
              strokeWidth='0.8'
            />

            {/* Band edge label */}
            {seg.label != null && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor='middle'
                fontSize='11'
                fontWeight='bold'
                fill='#000'
              >
                {seg.label}
              </text>
            )}

            {/* Share of the delivery in this band; 0% is shown, not hidden */}
            {showShares && (
              <text
                data-share
                x={mid.x}
                y={mid.y + 5}
                textAnchor='middle'
                fontSize='12'
                fontWeight='600'
                fill='#fff'
                pointerEvents='none'
              >
                {formatPercentage(seg.percentage)}
              </text>
            )}
          </g>
        );
      })}

      {/* Red needle: this delivery's average spekdikte (field 241) */}
      {zValue != null && (
        <path
          d={needlePath(needleAngle(zValue), INNER_R - 10, 5)}
          fill={NEEDLE_RED}
          filter={`url(#${SHADOW_FILTER_ID})`}
        />
      )}

      {/* Black needle: BPG benchmark average (drgmbmk DEGEMSD, week -1, ALL) */}
      {bpgValue != null && (
        <path
          d={needlePath(needleAngle(bpgValue), ((INNER_R - 10) * 3) / 4, 5)}
          fill={NEEDLE_BLACK}
          filter={`url(#${SHADOW_FILTER_ID})`}
        />
      )}

      <circle cx={CX} cy={CY} r={10} fill='#888' />
      <circle cx={CX} cy={CY} r={5} fill='#fff' />
    </svg>
  );
}

/**
 * The value as the backend sends it, so the footer reads exactly as specified
 * (field 241 arrives as '12,7', field 511 as '0'). Falls back to a formatted
 * number when the field is missing.
 */
function footerValue(raw) {
  if (raw != null && String(raw).trim() !== '') {
    return String(raw).trim();
  }
  return formatWeight(parseLocaleNumber(raw, 0));
}

function SpekdikteFooter({ rows }) {
  // In development and staging every row is shown, so the field wiring can be
  // checked against the payload; elsewhere a row with no value is dropped.
  const source = useFooterSource();
  const visibleRows = source.available
    ? rows
    : rows.filter((row) => !isBlankFooterValue(row.value));

  return (
    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.8', textAlign: 'center' }}>
      {visibleRows.map((row) => (
        <div
          key={row.label}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          {row.color ? (
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 2,
                background: row.color,
              }}
            />
          ) : (
            <span style={{ display: 'inline-block', width: 10 }} />
          )}
          <span>{`${row.label} (AVG: ${row.value})`}</span>
          <FieldSource source={row.source} shown={source.shown} />
        </div>
      ))}
      <FooterSourceToggle
        available={source.available}
        shown={source.shown}
        onToggle={source.toggle}
      />
    </div>
  );
}

/**
 * The gauge without its footer or shares, for a page that only has two averages
 * to show. A needle whose value is missing is not drawn.
 */
export function SpekdikteAverageGauge({ redValue, blackValue }) {
  return (
    <SpekdikteGauge
      segments={buildSpekdikteSegments(null)}
      zValue={redValue}
      bpgValue={blackValue}
      showShares={false}
    />
  );
}

/**
 * Spekdikte panel: gauge plus footer.
 *
 * Reads its own fields off the statistics payload so the caller only has to
 * supply the payload and the benchmark value.
 */
function SpekdikteChartPanel({ data, bpgValue, title }) {
  const segments = buildSpekdikteSegments(data ? data.klassement : null);
  const kt = data && data.klassement_totalen;

  // Red needle: field 241, the same spekdikte total the footer shows.
  const zValue = parseLocaleNumber(kt && kt['241']);

  // Every gauge lists its rows in the same order: Z, M, B, P, Totaal, BPG.
  const rows = [
    { label: 'Z', source: '191', value: footerValue(kt && kt['191']), color: null },
    { label: 'M', source: '221', value: footerValue(kt && kt['221']), color: null },
    { label: 'B', source: '511', value: footerValue(kt && kt['511']), color: null },
    { label: 'P', source: '536', value: footerValue(kt && kt['536']), color: null },
    { label: 'Totaal', source: '241', value: footerValue(kt && kt['241']), color: NEEDLE_RED },
  ];

  if (bpgValue != null) {
    rows.push({ label: 'BPG', source: 'BPG benchmark (drgmbmk, week -1, ALL)', value: formatWeight(bpgValue), color: NEEDLE_BLACK });
  }

  // The payload slice this gauge reads, for the source-data dialog.
  const source = {
    klassement: data ? data.klassement : null,
    klassement_totalen: pickFields(kt, ['241', '191', '511', '221', '536']),
    benchmark: { source: 'drgmbmk', field: 'DEGEMSD', deltmce: 'ALL', value: bpgValue },
  };

  return (
    <div className='statistics-chart-panel'>
      <div className='statistics-chart-header'>
        <h3>{title}</h3>
        <SourceDataButton title={title} source={source} />
      </div>
      <SpekdikteGauge segments={segments} zValue={zValue} bpgValue={bpgValue} />
      <SpekdikteFooter rows={rows} />
    </div>
  );
}

SpekdikteChartPanel.propTypes = {
  data: PropTypes.object,
  bpgValue: PropTypes.number,
  title: PropTypes.string,
};

SpekdikteChartPanel.defaultProps = {
  data: null,
  bpgValue: null,
  title: 'Spekdikte',
};

export default SpekdikteChartPanel;
